import { useLayoutEffect, useRef, useState } from 'react'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../../constants/common'
import { getIsGretaAIChatUserRole } from '../../../GretaAIChatMessage'
import {
  GRETA_PROGRAMMATIC_SCROLL_MS,
  GRETA_SCROLL_TOP_THRESHOLD,
  GRETA_SETTLE_PIN_MS,
} from '../../GretaAIChatMessageList.constants'

import {
  applyGretaAIChatSpacerHeight,
  getGretaAIChatAnchorScrollTop,
  getGretaAIChatBottomScrollTop,
  getGretaAIChatIsPinnedToBottom,
} from './useGretaAIChatAutoScroll.utils'

// Turn-anchored feed: a fresh question snaps to the top and stays there while the
// answer grows below it; a trailing spacer gives it the room.
export function useGretaAIChatAutoScroll(
  turns,
  { onLoadOlderMessages = EMPTY_FUNCTION } = EMPTY_OBJECT
) {
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true)

  const scrollRef = useRef(null)
  const anchorRef = useRef(null)
  const spacerRef = useRef(null)
  const previousCountRef = useRef(0)
  const spacerHeightRef = useRef(0)
  const programmaticScrollUntilRef = useRef(0)
  const bottomPinnedUntilRef = useRef(0)
  const olderScrollHeightRef = useRef(null)

  function isReaderScroll() {
    return performance.now() > programmaticScrollUntilRef.current
  }

  function isSettling() {
    return performance.now() < bottomPinnedUntilRef.current
  }

  function syncPinned(node) {
    setIsPinnedToBottom(
      getGretaAIChatIsPinnedToBottom(node, spacerHeightRef.current)
    )
  }

  function resizeSpacer(node) {
    spacerHeightRef.current = applyGretaAIChatSpacerHeight({
      anchor: anchorRef.current,
      node,
      spacer: spacerRef.current,
      spacerHeight: spacerHeightRef.current,
    })
  }

  // Marks the echo so it isn't mistaken for the reader taking over.
  function scrollTo(node, top) {
    programmaticScrollUntilRef.current =
      performance.now() + GRETA_PROGRAMMATIC_SCROLL_MS
    node.scrollTop = top
  }

  function handleScrollToBottom() {
    const node = scrollRef.current

    if (node) {
      scrollTo(
        node,
        getGretaAIChatBottomScrollTop(node, spacerHeightRef.current)
      )
      setIsPinnedToBottom(true)
    }
  }

  function anchorTurnToTop(node) {
    if (anchorRef.current) {
      resizeSpacer(node)
      scrollTo(node, getGretaAIChatAnchorScrollTop(node, anchorRef.current))
      syncPinned(node)
    } else {
      handleScrollToBottom()
    }
  }

  function handleScroll() {
    const node = scrollRef.current

    if (node) {
      syncPinned(node)

      if (isReaderScroll()) {
        bottomPinnedUntilRef.current = 0
      }

      if (
        node.scrollTop <= GRETA_SCROLL_TOP_THRESHOLD &&
        onLoadOlderMessages()
      ) {
        olderScrollHeightRef.current = node.scrollHeight
      }
    }
  }

  useLayoutEffect(() => {
    const node = scrollRef.current

    if (node) {
      if (olderScrollHeightRef.current !== null) {
        const addedHeight = node.scrollHeight - olderScrollHeightRef.current

        olderScrollHeightRef.current = null
        previousCountRef.current = turns.length
        scrollTo(node, node.scrollTop + addedHeight)
      } else {
        const previousCount = previousCountRef.current
        const lastTurn = turns[turns.length - 1]
        const isSingleAppend = turns.length === previousCount + 1
        const isSameCount = turns.length === previousCount
        const isNewQuestion =
          isSingleAppend && getIsGretaAIChatUserRole(lastTurn?.role)

        previousCountRef.current = turns.length

        if (isNewQuestion) {
          anchorTurnToTop(node)
        } else if (isSingleAppend || isSameCount) {
          resizeSpacer(node)
          syncPinned(node)
        } else {
          resizeSpacer(node)
          handleScrollToBottom()
          bottomPinnedUntilRef.current = performance.now() + GRETA_SETTLE_PIN_MS
        }
      }
    }
  }, [turns])

  useLayoutEffect(() => {
    const node = scrollRef.current

    if (node) {
      let frameId = null

      // Deferred and coalesced to a frame so mutating layout here can't re-enter
      // the observer ("ResizeObserver loop").
      const observer = new ResizeObserver(() => {
        if (frameId === null) {
          frameId = window.requestAnimationFrame(() => {
            frameId = null

            if (isSettling()) {
              handleScrollToBottom()
            }

            resizeSpacer(node)
            syncPinned(node)
          })
        }
      })

      observer.observe(node)

      if (node.firstElementChild) {
        observer.observe(node.firstElementChild)
      }

      return () => {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId)
        }

        observer.disconnect()
      }
    }
  }, [])

  return {
    anchorRef,
    handleScroll,
    handleScrollToBottom,
    scrollRef,
    showScrollToBottom: !isPinnedToBottom,
    spacerRef,
  }
}
